import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  Check,
  Plus,
  Minus,
  Map,
  CalendarX,
  Clock,
  ClipboardList,
  FileCheck,
  ShieldCheck,
  Radar,
  Landmark,
  Building2,
  TrendingUp,
  Briefcase,
  Users,
  RefreshCcw,
  BellRing,
} from "lucide-react";
import Navbar from "../../components/site/Navbar";
import Footer from "../../components/site/Footer";
import useReveal from "../../lib/useReveal";

const CALENDLY = "https://cal.com/rggsales/30min";

/* --------------------------------- Pieces --------------------------------- */

const Eyebrow = ({ children, tone = "green" }) =>
  tone === "green" ? (
    <span className="inline-flex items-center rounded-full bg-[#E7F6EE] border border-[#16A06A]/20 px-3.5 py-1.5">
      <span className="eyebrow text-[#16A06A]">{children}</span>
    </span>
  ) : (
    <span className="eyebrow text-slate-400">{children}</span>
  );

const PrimaryCTA = ({ children = "Book a Strategy Call", testid }) => (
  <a
    href={CALENDLY}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className="group inline-flex items-center justify-center gap-2 rounded-lg bg-[#16A06A] hover:bg-[#0E7C53] text-white px-6 py-3.5 text-[15px] font-semibold shadow-[0_1px_2px_rgba(15,23,42,0.08)] hover:shadow-[0_8px_24px_rgba(22,160,106,0.35)] transition-all"
  >
    {children}
    <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
  </a>
);

const MiniStat = ({ label, value }) => (
  <div className="rounded-lg bg-white/95 backdrop-blur border border-slate-200 px-3 py-2 card-soft">
    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
      {label}
    </div>
    <div className="text-[18px] font-bold text-[#0A1628] leading-tight">{value}</div>
  </div>
);

/* ---------------------------------- Hero ---------------------------------- */

const Hero = () => (
  <section data-testid="rfp-hero" className="relative pt-32 lg:pt-40 pb-20 lg:pb-24 overflow-hidden">
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
      <div className="absolute -top-32 -right-32 h-[480px] w-[480px] rounded-full bg-[rgba(22,160,106,0.08)] blur-3xl" />
      <div className="absolute top-32 -left-40 h-[420px] w-[420px] rounded-full bg-[rgba(10,22,40,0.05)] blur-3xl" />
    </div>

    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <Link
        to="/services"
        data-testid="rfp-hero-back"
        className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#16A06A] hover:text-[#0E7C53] reveal"
      >
        <ArrowLeft size={14} />
        All services
      </Link>

      <div className="mt-5 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-7 reveal">
          <Eyebrow>RFP &amp; Vendor Registration</Eyebrow>
          <h1 className="h1-hero mt-5 text-[#0A1628]">
            Get Registered, Stay Compliant,{" "}
            <span className="text-[#16A06A]">Win More Contracts.</span>
          </h1>
          <p className="mt-6 text-[18px] leading-[1.65] text-slate-500 max-w-[620px]">
            RevGen handles the paperwork, portals, and deadlines behind
            enterprise and public-sector procurement — so you stay eligible,
            respond faster, and win more of the RFPs that matter.
          </p>

          <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3">
            {[
              "Registered & compliant",
              "Portals managed",
              "Deadlines never missed",
            ].map((c) => (
              <div key={c} className="inline-flex items-center gap-2 text-[14px] text-slate-600">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#E7F6EE]">
                  <Check size={12} className="text-[#16A06A]" strokeWidth={3} />
                </span>
                {c}
              </div>
            ))}
          </div>

          <div className="mt-8">
            <PrimaryCTA testid="rfp-hero-cta" />
          </div>
        </div>

        <div className="lg:col-span-5 reveal">
          <div className="rounded-2xl bg-white card-soft border border-slate-200 overflow-hidden">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85"
                alt="Reviewing contracts and documents"
                loading="eager"
                className="w-full h-[400px] lg:h-[480px] object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white/85 to-transparent" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ---------------------------------- Stats --------------------------------- */

const STATS = [
  {
    title: "Registrations kept current",
    body: "Renewals, certifications and expiring documents tracked before they lapse.",
  },
  {
    title: "One reusable response library",
    body: "Capability statements and past-performance records assembled once, reused on every bid.",
  },
  {
    title: "Deadlines tracked, not remembered",
    body: "Submission dates managed on a calendar, not in someone's head.",
  },
  {
    title: "Documents complete and current",
    body: "Insurance, certifications and attachments organized and ready when a bid opens.",
  },
];

const Stats = () => (
  <section
    data-testid="rfp-stats"
    className="py-16 lg:py-20 bg-[#F8FAFC] border-y border-slate-200"
  >
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4">
        {STATS.map((s, i) => (
          <div
            key={s.title}
            className="reveal text-left lg:pl-8 lg:border-l border-slate-200 first:border-l-0 first:pl-0"
            style={{ transitionDelay: `${i * 70}ms` }}
          >
            <div className="text-[17px] lg:text-[18px] font-bold text-[#0A1628] leading-snug">
              {s.title}
            </div>
            <div className="mt-3 text-[14px] leading-relaxed text-slate-500">
              {s.body}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* --------------------------------- Problem -------------------------------- */

const PROBLEMS = [
  {
    icon: Map,
    title: "Registration Is a Maze",
    body:
      "Every agency and enterprise has its own portal, forms, and renewal cycles. Miss one and you're ineligible before you start.",
  },
  {
    icon: Clock,
    title: "RFPs Eat Your Week",
    body:
      "Responses are long, repetitive, and deadline-driven. Without a system, your team scrambles or skips the bid entirely.",
  },
  {
    icon: CalendarX,
    title: "Missed Deadlines = Missed Revenue",
    body:
      "A single late or incomplete submission can lock you out of a contract for years.",
  },
];

const Problem = () => (
  <section data-testid="rfp-problem" className="py-24 lg:py-28">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">The Issue</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">
          Procurement Is{" "}
          <span className="text-[#16A06A]">Leaving Money on the Table.</span>
        </h2>
        <p className="mt-5 text-[17px] leading-relaxed text-slate-500">
          Big contracts are won long before the RFP drops — through
          registration, relationships, and readiness. Most teams aren't set up
          for it.
        </p>
      </div>

      <div className="mt-14 grid md:grid-cols-3 gap-5">
        {PROBLEMS.map((p, i) => {
          const Icon = p.icon;
          return (
            <article
              key={p.title}
              data-testid={`rfp-problem-card-${i + 1}`}
              className="reveal group bg-white rounded-2xl border border-slate-200 p-7 card-soft hover:-translate-y-1 hover:border-[#16A06A]/40 transition-all"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[#E7F6EE] text-[#16A06A]">
                <Icon size={18} strokeWidth={2.4} />
              </div>
              <h3 className="mt-5 text-[19px] font-semibold text-[#0A1628] leading-snug">
                {p.title}
              </h3>
              <p className="mt-3 text-[15px] leading-relaxed text-slate-500">{p.body}</p>
            </article>
          );
        })}
      </div>
    </div>
  </section>
);

/* ------------------------------- What We Do ------------------------------- */

const CAPABILITIES = [
  {
    icon: ClipboardList,
    title: "Vendor Registration & Renewals",
    body:
      "We register your business across the public-sector and enterprise portals that matter (SAM.gov, state/local, and private vendor systems) and keep every profile current.",
  },
  {
    icon: FileCheck,
    title: "RFP/RFQ Response Management",
    body:
      "We track opportunities, build a reusable response library, draft and assemble submissions, and manage every deadline.",
  },
  {
    icon: ShieldCheck,
    title: "Compliance & Documentation",
    body:
      "Certifications, insurance, capability statements, and required attachments organized and kept compliance-ready.",
  },
  {
    icon: Radar,
    title: "Pipeline & Opportunity Tracking",
    body:
      "We monitor relevant bids and surface the ones worth pursuing, so you spend effort only where you can win.",
  },
];

const WhatWeDo = () => (
  <section data-testid="rfp-capabilities" className="py-24 lg:py-28 bg-[#F8FAFC]">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">The Solution</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">
          Procurement Readiness,{" "}
          <span className="text-[#16A06A]">End to End.</span>
        </h2>
      </div>

      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {CAPABILITIES.map((c, i) => {
          const Icon = c.icon;
          return (
            <article
              key={c.title}
              data-testid={`rfp-capability-${i + 1}`}
              className="reveal group bg-white rounded-2xl border border-slate-200 p-6 card-soft hover:-translate-y-1 hover:border-[#16A06A]/40 transition-all"
              style={{ transitionDelay: `${i * 70}ms` }}
            >
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#E7F6EE] text-[#16A06A] group-hover:bg-[#16A06A] group-hover:text-white transition-colors">
                <Icon size={18} />
              </div>
              <h3 className="mt-5 text-[18px] font-semibold text-[#0A1628]">{c.title}</h3>
              <p className="mt-3 text-[14px] leading-relaxed text-slate-500">{c.body}</p>
            </article>
          );
        })}
      </div>
    </div>
  </section>
);

/* ------------------------------ How It Works ------------------------------ */

const STEPS = [
  {
    n: "01",
    title: "Assess & Register",
    body:
      "We review where you should be registered and get you eligible across the right portals.",
  },
  {
    n: "02",
    title: "Build the Toolkit",
    body:
      "Capability statements, past-performance records, and a reusable response library are assembled once and reused on every bid.",
  },
  {
    n: "03",
    title: "Monitor & Respond",
    body:
      "We track relevant opportunities and manage each RFP/RFQ response end to end against the deadline.",
  },
  {
    n: "04",
    title: "Track & Renew",
    body:
      "We keep registrations current, follow up on submissions, and report on your bid pipeline.",
  },
];

const HowItWorks = () => (
  <section data-testid="rfp-how" className="py-24 lg:py-28">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">Process</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">From Eligible to Awarded.</h2>
      </div>

      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        {STEPS.map((s, i) => (
          <article
            key={s.n}
            data-testid={`rfp-step-${s.n}`}
            className="reveal relative bg-white rounded-2xl border border-slate-200 p-7 card-soft hover:-translate-y-1 hover:border-[#16A06A]/40 transition-all"
            style={{ transitionDelay: `${i * 70}ms` }}
          >
            <span className="text-[42px] font-bold leading-none text-[#16A06A]/20">
              {s.n}
            </span>
            <span className="eyebrow text-[#16A06A] block mt-3">
              Step {parseInt(s.n, 10)}
            </span>
            <h3 className="mt-2 text-[18px] font-semibold text-[#0A1628]">{s.title}</h3>
            <p className="mt-3 text-[14px] leading-relaxed text-slate-500">{s.body}</p>
          </article>
        ))}
      </div>
    </div>
  </section>
);

/* ------------------------------- Who It's For ----------------------------- */

const AUDIENCES = [
  {
    icon: Landmark,
    title: "Government & Public Sector Sellers",
    body: "Stay registered and compliant across federal, state, and local procurement.",
  },
  {
    icon: Building2,
    title: "Enterprise Vendors",
    body:
      "Navigate corporate vendor-onboarding portals and security/compliance requirements.",
  },
  {
    icon: TrendingUp,
    title: "Growing SMBs",
    body: "Punch above your weight on bids without hiring a full proposal team.",
  },
  {
    icon: Briefcase,
    title: "Professional Services Firms",
    body:
      "Keep certifications and capability statements current across many buyers.",
  },
];

const WhoItsFor = () => (
  <section data-testid="rfp-audiences" className="py-24 lg:py-28 bg-[#F8FAFC]">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">Who It's For</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">
          Built for Teams That Sell Into Complex Buyers.
        </h2>
      </div>

      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {AUDIENCES.map((a, i) => {
          const Icon = a.icon;
          return (
            <article
              key={a.title}
              data-testid={`rfp-audience-${i + 1}`}
              className="reveal group bg-white rounded-2xl border border-slate-200 p-6 card-soft hover:-translate-y-1 hover:border-[#16A06A]/40 transition-all"
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#E7F6EE] text-[#16A06A] group-hover:bg-[#16A06A] group-hover:text-white transition-colors">
                <Icon size={18} />
              </div>
              <h3 className="mt-5 text-[17px] font-semibold text-[#0A1628] leading-snug">
                {a.title}
              </h3>
              <p className="mt-3 text-[14px] leading-relaxed text-slate-500">{a.body}</p>
            </article>
          );
        })}
      </div>
    </div>
  </section>
);

/* ------------------------------- Why RevGen ------------------------------- */

const WHY = [
  {
    icon: Users,
    title: "A Dedicated, Managed Team",
    body:
      "A trained team owns your portals and deadlines, coordinated through one point of contact.",
  },
  {
    icon: RefreshCcw,
    title: "A Reusable Response Engine",
    body:
      "Build your proposal library once; respond to every future RFP faster and more consistently.",
  },
  {
    icon: BellRing,
    title: "Nothing Slips",
    body:
      "Calendars, renewals, and submission checklists are tracked so you never lose eligibility to a missed date.",
  },
];

const WhyRevGen = () => (
  <section data-testid="rfp-why" className="py-24 lg:py-28">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">Why RevGen</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">
          Built for Win Rates That <span className="text-[#16A06A]">Compound.</span>
        </h2>
      </div>

      <div className="mt-14 grid md:grid-cols-3 gap-5">
        {WHY.map((w, i) => {
          const Icon = w.icon;
          return (
            <article
              key={w.title}
              data-testid={`rfp-why-${i + 1}`}
              className="reveal group bg-white rounded-2xl border border-slate-200 p-7 card-soft hover:-translate-y-1 hover:border-[#16A06A]/40 transition-all"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#E7F6EE] text-[#16A06A] group-hover:bg-[#16A06A] group-hover:text-white transition-colors">
                <Icon size={18} />
              </div>
              <h3 className="mt-5 text-[19px] font-semibold text-[#0A1628]">{w.title}</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-slate-500">{w.body}</p>
            </article>
          );
        })}
      </div>
    </div>
  </section>
);

/* ----------------------------------- FAQ ---------------------------------- */

const FAQS = [
  {
    q: "Which portals do you register us on?",
    a: "Public-sector systems like SAM.gov and state/local registries, plus the private enterprise vendor portals relevant to your buyers. We assess and recommend during onboarding.",
  },
  {
    q: "Do you write the full RFP responses?",
    a: "Yes. We manage the response end to end — drafting, assembling attachments, and building a reusable library — with your input on technical and pricing sections.",
  },
  {
    q: "Can you keep our registrations and certifications current?",
    a: "Yes. We track renewal dates and required documentation so your profiles never lapse.",
  },
  {
    q: "What do you need from us?",
    a: "Company information, certifications, past-performance details, and sign-off on submissions. We handle the assembly and deadlines.",
  },
  {
    q: "How is this priced?",
    a: "Pricing depends on the number of portals and bid volume. Book a call and we'll scope it with you.",
  },
];

const FAQ = () => {
  const [open, setOpen] = useState(0);
  return (
    <section data-testid="rfp-faq" className="py-24 lg:py-28 bg-[#F8FAFC]">
      <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4 reveal">
            <Eyebrow tone="muted">FAQ</Eyebrow>
            <h2 className="h2-section mt-3 text-[#0A1628]">
              RFP & Vendor Registration — Frequently Asked Questions.
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-slate-500">
              Still unsure?{" "}
              <a
                href={CALENDLY}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#16A06A] font-semibold hover:text-[#0E7C53]"
              >
                Book a 30-min call →
              </a>
            </p>
          </div>

          <div className="lg:col-span-8 reveal">
            <div className="rounded-2xl border border-slate-200 bg-white card-soft divide-y divide-slate-200 overflow-hidden">
              {FAQS.map((f, i) => {
                const isOpen = open === i;
                return (
                  <div key={f.q} data-testid={`rfp-faq-item-${i + 1}`}>
                    <button
                      type="button"
                      onClick={() => setOpen(isOpen ? -1 : i)}
                      aria-expanded={isOpen}
                      data-testid={`rfp-faq-toggle-${i + 1}`}
                      className="w-full flex items-center justify-between gap-6 px-6 md:px-8 py-6 text-left hover:bg-[#FAFAFA] transition-colors"
                    >
                      <span className="text-[16px] md:text-[17px] font-semibold text-[#0A1628]">
                        {f.q}
                      </span>
                      <span
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors ${
                          isOpen
                            ? "bg-[#16A06A] text-white"
                            : "bg-[#E7F6EE] text-[#16A06A]"
                        }`}
                      >
                        {isOpen ? (
                          <Minus size={16} strokeWidth={3} />
                        ) : (
                          <Plus size={16} strokeWidth={3} />
                        )}
                      </span>
                    </button>
                    <div
                      className={`grid transition-all duration-300 ease-out ${
                        isOpen
                          ? "grid-rows-[1fr] opacity-100"
                          : "grid-rows-[0fr] opacity-0"
                      }`}
                    >
                      <div className="overflow-hidden">
                        <p className="px-6 md:px-8 pb-6 text-[15px] md:text-[16px] leading-relaxed text-slate-500">
                          {f.a}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* -------------------------------- Final CTA -------------------------------- */

const FinalCTA = () => (
  <section data-testid="rfp-final-cta" className="relative bg-[#0A1628] overflow-hidden">
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      <div className="absolute -top-24 left-1/4 h-[360px] w-[360px] rounded-full bg-[#16A06A]/15 blur-3xl" />
      <div className="absolute -bottom-32 right-10 h-[420px] w-[420px] rounded-full bg-[#16A06A]/10 blur-3xl" />
    </div>
    <div className="relative mx-auto max-w-[1200px] px-6 lg:px-8 py-24 lg:py-28 text-center">
      <div className="reveal">
        <span className="eyebrow text-[#16A06A]">Get started</span>
        <h2 className="h2-section mt-3 text-white max-w-3xl mx-auto">
          Ready to <span className="text-[#34D399]">Win More Bids?</span>
        </h2>
        <p className="mt-5 text-[17px] lg:text-[18px] leading-relaxed text-slate-300 max-w-2xl mx-auto">
          Book a 30-minute strategy call and we'll map your registration and
          RFP plan.
        </p>
        <div className="mt-9 flex justify-center">
          <PrimaryCTA testid="rfp-final-cta-btn" />
        </div>
      </div>
    </div>
  </section>
);

/* ---------------------------------- Page ---------------------------------- */

export default function RfpVendorRegistration() {
  useReveal();

  useEffect(() => {
    document.title = "RFP & Vendor Registration Strategies | RevGen";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        "content",
        "RevGen manages vendor registration, RFP/RFQ responses, and procurement compliance so you stay eligible and win more contracts. Book a strategy call."
      );
    }
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <div data-testid="rfp-vendor-registration-page" className="bg-white text-[#0A1628]">
      <Navbar />
      <main>
        <Hero />
        <Stats />
        <Problem />
        <WhatWeDo />
        <HowItWorks />
        <WhoItsFor />
        <WhyRevGen />
        <FAQ />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}
